/**
 * Componente para edição de nomes de participantes/speakers
 */

import React, { useState, useMemo } from 'react'
import {
  Box,
  Collapse,
  Grid,
  TextField,
  Typography
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CheckIcon from '@mui/icons-material/Check'
import { Transcription } from '../../../types/transcription'
import { SpeakerMapping } from '../../../services/exportService'

type Props = {
  transcription: Transcription
  speakerMapping: SpeakerMapping
  onSpeakerMappingChange: (mapping: SpeakerMapping) => void
}

export default function SpeakerEditor({ transcription, speakerMapping, onSpeakerMappingChange }: Props) {
  // Valores locais (em digitação) — evita que o trim() no onChange remova espaços intermediários
  const [localValues, setLocalValues] = useState<Record<string, string>>({})

  // Extrai speakers únicos da transcrição
  const uniqueSpeakers = useMemo(() => {
    const speakers = new Set<string>()
    for (const segment of transcription.transcript) {
      if (segment.speaker) {
        speakers.add(segment.speaker)
      }
    }
    return Array.from(speakers)
  }, [transcription])

  // Verifica se há speakers não identificados para inicializar o estado de expansão
  const [expanded, setExpanded] = useState(() => {
    return uniqueSpeakers.some(s => {
      const isGeneric = /^(Speaker|Falante|Participante)\s*\d+$/i.test(s) ||
        s === 'Não identificado' ||
        s === 'Unknown'

      if (isGeneric) {
        const mappedName = speakerMapping[s]
        return !mappedName || mappedName.trim() === ''
      }

      return false
    })
  })

  // Verifica se há speakers genéricos (Speaker 1, Speaker 2, etc.) que ainda não foram mapeados
  const hasGenericSpeakers = useMemo(() => {
    return uniqueSpeakers.some(s => {
      // Verifica se é um speaker genérico
      const isGeneric = /^(Speaker|Falante|Participante)\s*\d+$/i.test(s) ||
        s === 'Não identificado' ||
        s === 'Unknown'
      
      // Se for genérico, verifica se já tem um nome personalizado definido
      if (isGeneric) {
        const mappedName = speakerMapping[s]
        // Só considera "não identificado" se não tiver mapeamento ou se o mapeamento for vazio
        return !mappedName || mappedName.trim() === ''
      }
      
      return false
    })
  }, [uniqueSpeakers, speakerMapping])

  const handleSpeakerChange = (originalSpeaker: string, newName: string) => {
    const newMapping = { ...speakerMapping }
    if (newName.trim() && newName !== originalSpeaker) {
      newMapping[originalSpeaker] = newName.trim()
    } else {
      delete newMapping[originalSpeaker]
    }
    onSpeakerMappingChange(newMapping)
  }

  const hasMappings = Object.keys(speakerMapping).length > 0

  if (uniqueSpeakers.length === 0) {
    return null
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          p: 1.5,
          borderRadius: 1,
          bgcolor: hasGenericSpeakers ? '#fff3e0' : '#f5f5f5',
          border: hasGenericSpeakers ? '1px solid #ffcc80' : '1px solid #e0e0e0',
          '&:hover': { bgcolor: hasGenericSpeakers ? '#ffe0b2' : '#eeeeee' }
        }}
      >
        <PersonIcon sx={{ mr: 1.5, color: hasGenericSpeakers ? '#e65100' : '#666' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Identificação de Participantes
          </Typography>
          <Typography variant="caption" sx={{ color: '#666' }}>
            {hasGenericSpeakers 
              ? '⚠️ Há participantes não identificados. Clique para definir os nomes.'
              : hasMappings 
                ? `✓ ${Object.keys(speakerMapping).length} nome(s) personalizado(s)`
                : `${uniqueSpeakers.length} participante(s) identificado(s)`
            }
          </Typography>
        </Box>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2, p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Defina os nomes reais dos participantes. Os nomes serão aplicados em toda a transcrição e exportações:
          </Typography>
          
          <Grid container spacing={2}>
            {uniqueSpeakers.map((speaker) => (
              <Grid item xs={12} sm={6} md={4} key={speaker}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                    {speaker}
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Digite o nome real..."
                    value={speaker in localValues ? localValues[speaker] : (speakerMapping[speaker] || '')}
                    onChange={(e) => setLocalValues(prev => ({ ...prev, [speaker]: e.target.value }))}
                    onBlur={() => {
                      if (speaker in localValues) {
                        handleSpeakerChange(speaker, localValues[speaker])
                        setLocalValues(prev => {
                          const next = { ...prev }
                          delete next[speaker]
                          return next
                        })
                      }
                    }}
                    InputProps={{
                      startAdornment: (localValues[speaker]?.trim() || speakerMapping[speaker]) ? (
                        <CheckIcon sx={{ mr: 1, color: '#4caf50', fontSize: 18 }} />
                      ) : null
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#fff'
                      }
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Collapse>
    </Box>
  )
}
